import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { workerApi } from '../services/workerApi';
import { useChatbot } from '../context/ChatbotContext';
import {
  BookOpen, Plus, Search, Edit, Trash2, Image, X, Save,
  Tag, Upload, Sparkles, Check, Download, MessageCircle, Send, RotateCcw, Package,
  FileText, Loader2, CheckSquare, Square, ClipboardCheck, ThumbsUp, ThumbsDown, Brain,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface KBItem {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  image_url: string;
  category: string;
  is_active: boolean;
  status?: string;          // 'active' | 'pending_review'
  confidence?: number;      // 0-1, from agent_learning
  source?: string;          // 'pdf' | 'agent_learning'
  source_file?: string;     // original PDF filename
  source_page?: number;     // 1-based page number in source PDF
}

interface DraftItem {
  question: string;
  answer: string;
  keywords: string[];
  category: string;
  image_url?: string;
  is_active?: boolean;
  _include?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const CATEGORIES = ['Sản phẩm', 'Giao hàng', 'Thanh toán', 'Bảo hành', 'Chính sách', 'Liên hệ', 'Khác'];

const emptyForm = (): Partial<KBItem> => ({
  question: '',
  answer: '',
  keywords: [],
  image_url: '',
  category: '',
  is_active: true,
});

// ── CSV helpers ───────────────────────────────────────────────────────────────

const CSV_TEMPLATE = [
  'question,answer,keywords,category',
  '"Giao hàng bao lâu?","Chúng tôi giao hàng trong 2-3 ngày làm việc tại TP.HCM, 4-7 ngày các tỉnh thành khác.","giao hàng;ship;vận chuyển","Giao hàng"',
  '"Chính sách đổi trả?","Đổi trả trong vòng 7 ngày kể từ khi nhận hàng với điều kiện sản phẩm còn nguyên vẹn.","đổi trả;hoàn tiền","Chính sách"',
].join('\n');

const parseCSVLine = (line: string): string[] => {
  const cols: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    if (line[i] === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (line[i] === ',' && !inQ) {
      cols.push(cur); cur = '';
    } else {
      cur += line[i];
    }
  }
  cols.push(cur);
  return cols;
};

const parseCSV = (text: string): DraftItem[] => {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];
  return lines.slice(1).map(l => {
    const c = parseCSVLine(l);
    return {
      question: c[0]?.trim() || '',
      answer: c[1]?.trim() || '',
      keywords: c[2] ? c[2].split(';').map(k => k.trim()).filter(Boolean) : [],
      category: c[3]?.trim() || '',
      _include: true,
    };
  }).filter(r => r.question && r.answer);
};

const downloadTemplate = () => {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'kb_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

// ── Component ─────────────────────────────────────────────────────────────────

export const KnowledgeBase: React.FC = () => {
  const { t } = useTranslation('knowledge');
  const { account } = useChatbot();
  const pageId = account?.platform_id || '';

  // ── List ────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<KBItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // ── Manual form modal ───────────────────────────────────────────────────────
  const [showManual, setShowManual] = useState(false);
  const [editItem, setEditItem] = useState<KBItem | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [keywordInput, setKeywordInput] = useState('');
  const [saving, setSaving] = useState(false);

  // ── CSV import modal ────────────────────────────────────────────────────────
  const [showCSV, setShowCSV] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvDraft, setCsvDraft] = useState<DraftItem[]>([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvDone, setCsvDone] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── PDF import modal ────────────────────────────────────────────────
  const [showPdf, setShowPdf] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfEstimate, setPdfEstimate] = useState<any | null>(null);
  const [pdfEstimating, setPdfEstimating] = useState(false);
  const [pdfExtractImages, setPdfExtractImages] = useState(true);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfResult, setPdfResult] = useState<{ text: number; image: number; file: string } | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);

  // ── AI extract modal ────────────────────────────────────────────────────────
  const [showAI, setShowAI] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiDraft, setAiDraft] = useState<DraftItem[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiImporting, setAiImporting] = useState(false);
  const [aiDone, setAiDone] = useState(0);
  // ── Interview modal ────────────────────────────────────────────────────────
  const [showInterview, setShowInterview] = useState(false);
  const [ivHistory, setIvHistory] = useState<{role: string; content: string}[]>([]);
  const [ivInput, setIvInput] = useState('');
  const [ivLoading, setIvLoading] = useState(false);
  const [ivItems, setIvItems] = useState<DraftItem[]>([]);
  const [ivProducts, setIvProducts] = useState<{name: string; description: string; price: string; _include?: boolean}[]>([]);
  const [ivDone, setIvDone] = useState(false);
  const [ivImporting, setIvImporting] = useState(false);
  const [ivSaved, setIvSaved] = useState(0);
  const [ivError, setIvError] = useState('');
  const ivEndRef = useRef<HTMLDivElement>(null);
  // ── Tab ─────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');

  // ── Pending review state ────────────────────────────────────────────────────
  const [pendingItems, setPendingItems] = useState<KBItem[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());

  // ── Data ────────────────────────────────────────────────────────────────────
  useEffect(() => { fetchItems(); fetchPending(); }, [pageId]);

  const fetchItems = async (q?: string) => {
    try {
      const res: any = q
        ? await workerApi.searchKnowledgeBase(q, pageId)
        : await workerApi.getKnowledgeBaseItems(pageId);
      setItems(res.data || []);
    } catch (e) {
      console.error('KB fetch failed', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPending = async () => {
    setPendingLoading(true);
    try {
      const res: any = await workerApi.getPendingKBItems(pageId || undefined);
      setPendingItems(res.data || []);
    } catch (e) {
      console.error('Pending KB fetch failed', e);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleReview = async (id: string, action: 'approve' | 'reject') => {
    setReviewingId(id);
    try {
      await workerApi.reviewKBItem(id, action);
      setReviewedIds(s => new Set(s).add(id));
      // Remove from pending list after short delay for UX feedback
      setTimeout(() => {
        setPendingItems(prev => prev.filter(p => p.id !== id));
        setReviewedIds(s => { const n = new Set(s); n.delete(id); return n; });
        if (action === 'approve') fetchItems();
      }, 800);
    } catch (e) {
      console.error('Review KB failed', e);
    } finally {
      setReviewingId(null);
    }
  };

  // ── Manual form ─────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditItem(null); setForm(emptyForm()); setKeywordInput(''); setShowManual(true);
  };
  const openEdit = (item: KBItem) => {
    setEditItem(item); setForm({ ...item }); setKeywordInput(''); setShowManual(true);
  };
  const closeManual = () => { setShowManual(false); setEditItem(null); };
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !form.keywords?.includes(kw))
      setForm(f => ({ ...f, keywords: [...(f.keywords || []), kw] }));
    setKeywordInput('');
  };
  const removeKeyword = (kw: string) =>
    setForm(f => ({ ...f, keywords: (f.keywords || []).filter(k => k !== kw) }));
  const handleManualSave = async () => {
    if (!form.question?.trim() || !form.answer?.trim()) return;
    setSaving(true);
    try {
      if (editItem) await workerApi.updateKnowledgeBaseItem(editItem.id, form);
      else await workerApi.createKnowledgeBaseItem(form, pageId);
      closeManual();
      fetchItems(searchQuery.trim() || undefined);
    } finally { setSaving(false); }
  };

  // ── CSV import ──────────────────────────────────────────────────────────────
  const handleCSVFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const text = ev.target?.result as string;
      setCsvText(text);
      setCsvDraft(parseCSV(text));
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };
  const handleCSVImport = async () => {
    const toImport = csvDraft.filter(r => r._include !== false);
    if (!toImport.length) return;
    setCsvImporting(true);
    try {
      const res: any = await workerApi.bulkImportKB(toImport, pageId);
      setCsvDone(res.created || 0);
      setTimeout(() => { setShowCSV(false); setCsvText(''); setCsvDraft([]); setCsvDone(0); fetchItems(); }, 1800);
    } catch (e) { console.error('CSV import failed', e); }
    finally { setCsvImporting(false); }
  };

  // ── AI extract ──────────────────────────────────────────────────────────────
  const handleAIExtract = async () => {
    if (!aiText.trim()) return;
    setAiLoading(true); setAiError(''); setAiDraft([]);
    try {
      const res: any = await workerApi.extractKBFromText(aiText, pageId);
      setAiDraft((res.data || []).map((d: any) => ({ ...d, _include: true })));
      if ((res.data || []).length === 0) setAiError(t('aiNoResults'));
    } catch (e: any) {
      setAiError(e?.response?.data?.detail || t('extractFailed'));
    } finally { setAiLoading(false); }
  };
  const toggleAIDraft = (idx: number) =>
    setAiDraft(d => d.map((it, i) => i === idx ? { ...it, _include: !it._include } : it));
  const handleAIImport = async () => {
    const toImport = aiDraft.filter(r => r._include !== false);
    if (!toImport.length) return;
    setAiImporting(true);
    try {
      const res: any = await workerApi.bulkImportKB(toImport, pageId);
      setAiDone(res.created || 0);
      setTimeout(() => { setShowAI(false); setAiText(''); setAiDraft([]); setAiDone(0); fetchItems(); }, 1800);
    } catch (e) { console.error('AI import failed', e); }
    finally { setAiImporting(false); }
  };

  // ── Interview ──────────────────────────────────────────────────────────────
  const openInterview = () => {
    setShowInterview(true); setIvHistory([]); setIvInput(''); setIvItems([]); setIvProducts([]); setIvDone(false); setIvSaved(0);
    // Auto-send first message to kick off the interview
    (async () => {
      setIvLoading(true);
      try {
        const res: any = await workerApi.interviewKB('Bắt đầu phỏng vấn', []);
        setIvHistory([{ role: 'assistant', content: res.reply }]);
      } catch { setIvHistory([{ role: 'assistant', content: 'Xin chào! Doanh nghiệp bạn hoạt động trong lĩnh vực gì?' }]); }
      finally { setIvLoading(false); }
    })();
  };
  const sendInterviewMsg = async () => {
    const msg = ivInput.trim();
    if (!msg || ivLoading) return;
    const newHistory = [...ivHistory, { role: 'user', content: msg }];
    setIvHistory(newHistory); setIvInput(''); setIvLoading(true); setIvError('');
    setTimeout(() => ivEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      const res: any = await workerApi.interviewKB(msg, newHistory);
      if (res.done && res.items?.length) {
        setIvHistory(h => [...h, { role: 'assistant', content: res.reply || `Đã tạo ${res.items.length} Q&A!` }]);
        setIvItems(res.items.map((it: any) => ({ ...it, _include: true })));
        if (res.products?.length) {
          setIvProducts(res.products.map((p: any) => ({ ...p, _include: true })));
        }
        setIvDone(true);
      } else {
        setIvHistory(h => [...h, { role: 'assistant', content: res.reply }]);
      }
    } catch {
      setIvHistory(h => h.slice(0, -1));
      setIvInput(msg);
      setIvError('Có lỗi xảy ra, vui lòng thử lại.');
      setTimeout(() => setIvError(''), 4000);
    } finally {
      setIvLoading(false);
      setTimeout(() => ivEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };
  const handleIvImport = async () => {
    const toImport = ivItems.filter(r => r._include !== false);
    const prodsToImport = ivProducts.filter(r => r._include !== false);
    if (!toImport.length && !prodsToImport.length) return;
    setIvImporting(true);
    try {
      let kbCreated = 0;
      if (toImport.length) {
        const res: any = await workerApi.bulkImportKB(toImport, pageId);
        kbCreated = res.created || 0;
      }
      if (prodsToImport.length) {
        const cfg: any = await workerApi.getBotConfig(pageId);
        const existing = cfg.products || [];
        const merged = [...existing, ...prodsToImport.map(p => ({
          name: p.name, description: p.description, price: p.price, image_urls: [], ref_url: '',
        }))];
        await workerApi.patchBotConfig(pageId, { products: merged });
      }
      setIvSaved(kbCreated + prodsToImport.length);
      setTimeout(() => { setShowInterview(false); fetchItems(); }, 1800);
    } catch (e) { console.error('Interview import failed', e); }
    finally { setIvImporting(false); }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (id: string) => {
    await workerApi.deleteKnowledgeBaseItem(id);
    setDeleteConfirm(null);
    fetchItems(searchQuery.trim() || undefined);
  };

  // ── Multi-select & bulk delete ─────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const toggleSelect = (id: string) =>
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleSelectAll = () =>
    setSelected(s => s.size === items.length ? new Set() : new Set(items.map(i => i.id)));

  const clearSelection = () => { setSelected(new Set()); setBulkConfirm(false); };

  const handleBulkDelete = async () => {
    if (!selected.size) return;
    setBulkDeleting(true);
    try {
      await workerApi.bulkDeleteKB(Array.from(selected));
      clearSelection();
      fetchItems(searchQuery.trim() || undefined);
    } catch (e) { console.error('Bulk delete failed', e); }
    finally { setBulkDeleting(false); setBulkConfirm(false); }
  };
  // ── PDF import ────────────────────────────────────────────────────────────────
  const openPdf = () => {
    setShowPdf(true);
    setPdfFile(null);
    setPdfExtractImages(true);
    setPdfError('');
    setPdfResult(null);
    setPdfEstimate(null);
  };
  const fetchPdfEstimate = async (f: File, extractImages: boolean) => {
    setPdfEstimating(true);
    setPdfEstimate(null);
    try {
      const res = await workerApi.estimatePdfCost({ file: f, extractImages });
      setPdfEstimate(res.estimate);
    } catch (e: any) {
      setPdfError(e?.response?.data?.detail || e?.message || 'Khong uoc luong duoc chi phi');
    } finally {
      setPdfEstimating(false);
    }
  };
  const handlePdfPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) {
      setPdfError(t('pdfOnly'));
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setPdfError(t('fileTooLarge'));
      return;
    }
    setPdfError('');
    setPdfFile(f);
    fetchPdfEstimate(f, pdfExtractImages);
  };
  const handlePdfImport = async () => {
    if (!pdfFile) return;
    setPdfUploading(true);
    setPdfError('');
    setPdfResult(null);
    try {
      const res = await workerApi.importKbPdf({
        file: pdfFile,
        channelId: pageId,
        extractImages: pdfExtractImages,
      });
      setPdfResult({ text: res.text_chunks, image: res.image_entries, file: res.source_file });
      setTimeout(() => {
        setShowPdf(false);
        setPdfFile(null);
        setPdfResult(null);
        fetchItems();
      }, 2200);
    } catch (e: any) {
      setPdfError(e?.response?.data?.detail || e?.message || t('importPdfFailed'));
    } finally {
      setPdfUploading(false);
    }
  };
  // ── Render ──────────────────────────────────────────────────────────────────
  if (isLoading) return <div className="flex items-center justify-center h-64 text-sm text-gray-400">{t('loading')}</div>;

  const selectedCSV = csvDraft.filter(r => r._include !== false).length;
  const selectedAI  = aiDraft.filter(r => r._include !== false).length;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t('title')}</h1>
          <p className="text-xs text-gray-400 mt-0.5">{t('subtitle')}</p>
        </div>
        {activeTab === 'active' && (
          <div className="flex flex-wrap gap-2">
            <button onClick={openCreate}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors">
              <Plus size={14} /> {t('addManual')}
            </button>
            <button onClick={() => { setShowCSV(true); setCsvDraft([]); setCsvText(''); setCsvDone(0); }}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
              <Upload size={14} /> {t('importCsv')}
            </button>
            <button onClick={openPdf}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors">
              <FileText size={14} /> {t('importPdf')}
            </button>
            <button onClick={() => { setShowAI(true); setAiDraft([]); setAiText(''); setAiError(''); setAiDone(0); }}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-violet-500 text-white rounded-lg text-sm font-medium hover:bg-violet-600 transition-colors">
              <Sparkles size={14} /> {t('importAi')}
            </button>
            <button onClick={openInterview}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors">
              <MessageCircle size={14} /> {t('interview')}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <BookOpen size={14} /> {t('approved')}
          <span className="ml-1 px-1.5 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-500">{items.length}</span>
        </button>
        <button
          onClick={() => { setActiveTab('pending'); fetchPending(); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pending'
              ? 'border-amber-500 text-amber-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}>
          <Brain size={14} /> Chờ duyệt (AI)
          {pendingItems.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-700 font-semibold">
              {pendingItems.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'active' && (<>
      {/* Search */}
      <div className="relative max-w-lg">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchItems(searchQuery.trim() || undefined)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300" />
        {searchQuery && (
          <button onClick={() => { setSearchQuery(''); fetchItems(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={13} /></button>
        )}
      </div>

      {/* Bulk-select toolbar — visible when ≥1 item selected */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl">
          <button onClick={toggleSelectAll} className="text-gray-500 hover:text-gray-700">
            {selected.size === items.length
              ? <CheckSquare size={16} className="text-blue-500" />
              : <Square size={16} />}
          </button>
          <span className="text-sm font-medium text-gray-700 flex-1">
            Đã chọn <b>{selected.size}</b> / {items.length} mục
          </span>
          <button onClick={clearSelection}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600">
            Bỏ chọn
          </button>
          {!bulkConfirm ? (
            <button onClick={() => setBulkConfirm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500 text-white hover:bg-red-600">
              <Trash2 size={13} /> Xóa {selected.size} mục
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600 font-medium">Xác nhận xóa {selected.size} mục?</span>
              <button onClick={() => setBulkConfirm(false)}
                className="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50">Huỷ</button>
              <button onClick={handleBulkDelete} disabled={bulkDeleting}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60">
                {bulkDeleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                {bulkDeleting ? 'Đang xóa...' : 'Xóa ngay'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <BookOpen size={40} className="text-gray-300 mb-3" />
          <p className="text-sm font-medium text-gray-500">{t('noItems')}</p>
          <p className="text-xs text-gray-400 mt-1">Thêm Q&amp;A để chatbot trả lời thông minh hơn</p>
          <div className="flex flex-wrap gap-2 mt-4 justify-center">
            <button onClick={openCreate} className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600">+ {t('addManual')}</button>
            <button onClick={() => { setShowAI(true); setAiDraft([]); setAiText(''); setAiError(''); setAiDone(0); }}
              className="flex items-center gap-1.5 px-4 py-2 bg-violet-500 text-white text-sm rounded-lg hover:bg-violet-600">
              <Sparkles size={13} /> {t('importAi')}
            </button>
            <button onClick={openInterview}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 text-white text-sm rounded-lg hover:bg-emerald-600">
              <MessageCircle size={13} /> {t('interview')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Select-all row */}
          <div className="flex items-center gap-2 px-1">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700">
              {selected.size === items.length && items.length > 0
                ? <CheckSquare size={15} className="text-blue-500" />
                : <Square size={15} />}
              {selected.size === items.length && items.length > 0 ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
            </button>
            <span className="text-xs text-gray-400">({items.length} mục)</span>
          </div>

          {items.map(item => (
            <div key={item.id}
              className={`bg-white rounded-xl border shadow-sm p-4 transition-colors ${
                selected.has(item.id) ? 'border-blue-300 bg-blue-50/30' : 'border-gray-100'
              }`}>
              <div className="flex items-start gap-3">
                {/* Checkbox */}
                <button onClick={() => toggleSelect(item.id)}
                  className="mt-0.5 shrink-0 text-gray-400 hover:text-blue-500">
                  {selected.has(item.id)
                    ? <CheckSquare size={16} className="text-blue-500" />
                    : <Square size={16} />}
                </button>
                {item.image_url && (
                  <img src={item.image_url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-200 shrink-0"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                        <BookOpen size={13} className="text-blue-500 shrink-0" />{item.question}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.answer}</p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => openEdit(item)} title="Sửa"
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"><Edit size={14} /></button>
                      <button onClick={() => setDeleteConfirm(item.id)} title="Xóa"
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {item.category && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600">{item.category}</span>}
                    {item.source === 'pdf' && item.source_file && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-50 text-orange-700" title={item.source_file}>
                        <FileText size={11} /> PDF · trang {item.source_page ?? '?'}
                      </span>
                    )}
                    {item.keywords.map(kw => <span key={kw} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-500">#{kw}</span>)}
                    {item.image_url && <span className="flex items-center gap-1 text-[11px] text-emerald-600"><Image size={11} /> Có ảnh</span>}
                    {!item.is_active && <span className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-400">Tắt</span>}
                  </div>
                </div>
              </div>
              {deleteConfirm === item.id && (
                <div className="mt-3 pt-3 border-t border-red-100 flex items-center justify-between">
                  <p className="text-xs text-red-600">Xác nhận xóa mục này?</p>
                  <div className="flex gap-2">
                    <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Huỷ</button>
                    <button onClick={() => handleDelete(item.id)} className="px-3 py-1 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600">Xóa</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>)}

      {/* ── Pending Review Tab ──────────────────────────────────────────────── */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {pendingLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Đang tải danh sách chờ duyệt...</span>
            </div>
          ) : pendingItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ClipboardCheck size={40} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-500">Không có mục nào chờ duyệt</p>
              <p className="text-xs text-gray-400 mt-1">Chatbot sẽ tự học khi agent xử lý hội thoại</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  <span className="font-medium text-amber-700">{pendingItems.length} mục</span> cần xem xét — do chatbot tự học từ phiên agent
                </p>
                <button onClick={fetchPending} className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
                  <RotateCcw size={12} /> Làm mới
                </button>
              </div>
              {pendingItems.map(item => {
                const isDone = reviewedIds.has(item.id);
                const isReviewing = reviewingId === item.id;
                const confidence = item.confidence ?? 0;
                const confPct = Math.round(confidence * 100);
                return (
                  <div key={item.id}
                    className={`bg-amber-50 border rounded-xl p-4 transition-all ${isDone ? 'opacity-50 scale-[0.99]' : 'border-amber-200'}`}>
                    <div className="flex items-start gap-3">
                      <div className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Brain size={16} className="text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{item.question}</p>
                        <p className="text-sm text-gray-600 mt-1 leading-relaxed">{item.answer}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2.5">
                          {item.category && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600">{item.category}</span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white border ${
                            confidence >= 0.85 ? 'border-emerald-200 text-emerald-700'
                            : confidence >= 0.65 ? 'border-amber-200 text-amber-700'
                            : 'border-red-200 text-red-600'
                          }`}>
                            Độ tin cậy {confPct}%
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[11px] bg-violet-50 text-violet-600 font-medium">
                            Chatbot tự học
                          </span>
                          {item.keywords?.map(kw => (
                            <span key={kw} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-500">#{kw}</span>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        {isDone ? (
                          <span className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 text-gray-400">Đã xử lý</span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleReview(item.id, 'approve')}
                              disabled={isReviewing}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60">
                              {isReviewing ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} />}
                              Duyệt
                            </button>
                            <button
                              onClick={() => handleReview(item.id, 'reject')}
                              disabled={isReviewing}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60">
                              {isReviewing ? <Loader2 size={12} className="animate-spin" /> : <ThumbsDown size={12} />}
                              Từ chối
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* ── Modal: Thêm thủ công ─────────────────────────────────────────────── */}
      {showManual && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">{editItem ? 'Sửa Q&A' : 'Thêm Q&A mới'}</h2>
              <button onClick={closeManual} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Câu hỏi <span className="text-red-500">*</span></label>
                <input type="text" value={form.question || ''}
                  onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                  placeholder="VD: Sản phẩm này có màu gì?"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Câu trả lời <span className="text-red-500">*</span></label>
                <textarea rows={3} value={form.answer || ''}
                  onChange={e => setForm(f => ({ ...f, answer: e.target.value }))}
                  placeholder="VD: Sản phẩm có 3 màu: đen, trắng và đỏ."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 resize-y min-h-[80px]" />
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1">
                  <Image size={12} /> URL ảnh minh hoạ <span className="text-gray-400 font-normal">(tuỳ chọn)</span>
                </label>
                <input type="url" value={form.image_url || ''}
                  onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                  placeholder="https://example.com/product.jpg"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300" />
                {form.image_url && (
                  <img src={form.image_url} alt="preview"
                    className="mt-2 h-24 rounded-lg object-cover border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity"
                    onClick={() => setLightboxSrc(form.image_url || null)}
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
                <p className="text-[11px] text-gray-400 mt-1">Bot sẽ gửi ảnh này kèm câu trả lời khi khách hỏi.</p>
              </div>
              <div>
                <label className="flex items-center gap-1 text-xs font-medium text-gray-600 mb-1"><Tag size={12} /> Từ khoá kích hoạt</label>
                <div className="flex gap-2">
                  <input type="text" value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                    placeholder="Nhập từ khoá rồi Enter"
                    className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300" />
                  <button onClick={addKeyword} className="px-3 py-2 text-xs bg-gray-100 rounded-lg hover:bg-gray-200 text-gray-600">Thêm</button>
                </div>
                {(form.keywords || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(form.keywords || []).map(kw => (
                      <span key={kw} className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs">
                        #{kw}<button onClick={() => removeKeyword(kw)} className="hover:text-red-500"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Danh mục</label>
                <select value={form.category || ''} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white">
                  <option value="">-- Chọn danh mục --</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_active ?? true}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded accent-blue-500" />
                <span className="text-sm text-gray-700">Kích hoạt (bot sẽ dùng mục này)</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button onClick={closeManual} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">Huỷ</button>
              <button onClick={handleManualSave} disabled={saving || !form.question?.trim() || !form.answer?.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                <Save size={14} /> {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Nhập CSV ───────────────────────────────────────────────────── */}
      {showCSV && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Nhập CSV</h2>
                <p className="text-xs text-gray-400 mt-0.5">Paste nội dung CSV hoặc chọn file .csv</p>
              </div>
              <button onClick={() => setShowCSV(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X size={16} /></button>
            </div>
            {csvDone > 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Check size={22} className="text-green-600" /></div>
                <p className="text-sm font-medium text-gray-700">Đã nhập <strong>{csvDone}</strong> mục thành công!</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors">
                      <Upload size={14} /> Chọn file .csv
                    </button>
                    <button onClick={downloadTemplate}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">
                      <Download size={14} /> Tải template
                    </button>
                    <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleCSVFile} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Nội dung CSV</label>
                    <textarea rows={7} value={csvText}
                      onChange={e => { setCsvText(e.target.value); setCsvDraft([]); }}
                      placeholder={"question,answer,keywords,category\n\"Giao hàng bao lâu?\",\"2-3 ngày\",\"giao hàng;ship\",\"Giao hàng\""}
                      className="w-full px-3 py-2 text-xs font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-300 resize-none" />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Cột: <code className="bg-gray-100 px-1 rounded">question</code>, <code className="bg-gray-100 px-1 rounded">answer</code>, <code className="bg-gray-100 px-1 rounded">keywords</code> (phân cách <code className="bg-gray-100 px-1 rounded">;</code>), <code className="bg-gray-100 px-1 rounded">category</code>
                    </p>
                  </div>
                  <button onClick={() => setCsvDraft(parseCSV(csvText))}
                    className="px-4 py-2 text-sm rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium">
                    {csvDraft.length > 0 ? `Xem trước (${csvDraft.length} mục)` : 'Xem trước'}
                  </button>
                  {csvDraft.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-60 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-left w-8">
                                <input type="checkbox" className="accent-blue-500"
                                  checked={csvDraft.every(r => r._include !== false)}
                                  onChange={e => setCsvDraft(d => d.map(r => ({ ...r, _include: e.target.checked })))} />
                              </th>
                              <th className="px-3 py-2 text-left text-gray-500 font-medium">Câu hỏi</th>
                              <th className="px-3 py-2 text-left text-gray-500 font-medium">Câu trả lời</th>
                              <th className="px-3 py-2 text-left text-gray-500 font-medium">Danh mục</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {csvDraft.map((row, i) => (
                              <tr key={i} className={row._include === false ? 'opacity-40' : ''}>
                                <td className="px-3 py-2">
                                  <input type="checkbox" className="accent-blue-500"
                                    checked={row._include !== false}
                                    onChange={() => setCsvDraft(d => d.map((r, j) => j === i ? { ...r, _include: !r._include } : r))} />
                                </td>
                                <td className="px-3 py-2 text-gray-700 max-w-[160px] truncate">{row.question}</td>
                                <td className="px-3 py-2 text-gray-500 max-w-[200px] truncate">{row.answer}</td>
                                <td className="px-3 py-2 text-gray-400">{row.category}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 shrink-0">
                  <button onClick={() => setShowCSV(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">Huỷ</button>
                  <button onClick={handleCSVImport} disabled={csvImporting || selectedCSV === 0}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                    <Upload size={14} /> {csvImporting ? 'Đang nhập...' : `Nhập ${selectedCSV} mục`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: Nhập PDF ──────────────────────────────────────────────────── */}
      {showPdf && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <FileText size={16} className="text-orange-500" /> Nhập từ file PDF
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Trích xuất text + ảnh, embed vào Knowledge Base.</p>
              </div>
              <button onClick={() => !pdfUploading && setShowPdf(false)}
                className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50" disabled={pdfUploading}>
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={handlePdfPick}
              />

              {!pdfFile ? (
                <button
                  onClick={() => pdfInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-300 hover:border-orange-400 rounded-xl py-10 flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-orange-600 transition-colors"
                >
                  <Upload size={28} />
                  <span className="text-sm font-medium">Chọn file PDF</span>
                  <span className="text-xs text-gray-400">Tối đa 20MB</span>
                </button>
              ) : (
                <div className="border border-gray-200 rounded-xl p-4 flex items-center gap-3">
                  <FileText size={28} className="text-orange-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{pdfFile.name}</p>
                    <p className="text-xs text-gray-400">{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  {!pdfUploading && (
                    <button onClick={() => setPdfFile(null)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-500">
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={pdfExtractImages}
                  onChange={e => {
                    const v = e.target.checked;
                    setPdfExtractImages(v);
                    if (pdfFile) fetchPdfEstimate(pdfFile, v);
                  }}
                  disabled={pdfUploading}
                  className="mt-0.5"
                />
                <div>
                  <p className="font-medium">Trích xuất ảnh (Gemini Vision)</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Clip ảnh trong PDF + AI mô tả để chatbot có thể tìm kiếm theo ngữ nghĩa.
                    Quá trình này chậm hơn (~4s/ảnh) và dùng quota Gemini.
                  </p>
                </div>
              </label>

              {/* Cost estimate panel */}
              {pdfFile && pdfEstimating && (
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-600 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span>Đang phân tích PDF và ước lượng chi phí...</span>
                </div>
              )}
              {pdfFile && pdfEstimate && !pdfEstimating && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                    <Sparkles size={14} /> Ơớc lượng chi phí Gemini API
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-700">
                    <div>Số trang PDF:</div>
                    <div className="text-right font-medium">{pdfEstimate.page_count}</div>
                    <div>Text chunks:</div>
                    <div className="text-right font-medium">{pdfEstimate.text_chunks}</div>
                    <div>Số ảnh:</div>
                    <div className="text-right font-medium">{pdfEstimate.image_count}</div>
                    <div>Vision API calls:</div>
                    <div className="text-right font-medium">{pdfEstimate.vision_calls}</div>
                    <div>Embedding calls:</div>
                    <div className="text-right font-medium">{pdfEstimate.embed_calls}</div>
                    <div>Thời gian dự kiến:</div>
                    <div className="text-right font-medium">~{Math.round(pdfEstimate.estimated_seconds / 60)} phút</div>
                  </div>
                  <div className="pt-2 border-t border-amber-200 flex items-baseline justify-between">
                    <span className="text-sm font-semibold text-amber-900">Tổng chi phí:</span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-amber-900">
                        ~{pdfEstimate.total_cost_vnd.toLocaleString('vi-VN')} đ
                      </div>
                      <div className="text-[11px] text-amber-700">
                        ~${pdfEstimate.total_cost_usd.toFixed(4)} USD
                      </div>
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-700 italic">
                    {pdfEstimate.note}
                  </p>
                </div>
              )}

              {pdfError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-600">
                  {pdfError}
                </div>
              )}

              {pdfResult && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-emerald-700 flex items-center gap-2">
                  <Check size={16} />
                  <span>
                    Đã nhập <b>{pdfResult.text}</b> đoạn text + <b>{pdfResult.image}</b> ảnh từ &quot;{pdfResult.file}&quot;.
                  </span>
                </div>
              )}

              {pdfUploading && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-700 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Đang xử lý PDF... (text nhanh, mỗi ảnh ~4 giây nếu bật Vision)</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setShowPdf(false)}
                disabled={pdfUploading}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 disabled:opacity-50"
              >
                Huỷ
              </button>
              <button
                onClick={handlePdfImport}
                disabled={!pdfFile || pdfUploading || !pageId || pdfEstimating}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pdfUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {pdfUploading
                  ? 'Đang xử lý...'
                  : pdfEstimate
                    ? `Đồng ý & nhập (~${pdfEstimate.total_cost_vnd.toLocaleString('vi-VN')}đ)`
                    : 'Bắt đầu nhập'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Nhập bằng AI ───────────────────────────────────────────────── */}
      {showAI && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div>
                <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <Sparkles size={16} className="text-violet-500" /> Nhập bằng AI
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">Paste bất kỳ đoạn văn — AI tự tạo Q&amp;A cho chatbot</p>
              </div>
              <button onClick={() => setShowAI(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X size={16} /></button>
            </div>
            {aiDone > 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center"><Check size={22} className="text-green-600" /></div>
                <p className="text-sm font-medium text-gray-700">Đã nhập <strong>{aiDone}</strong> mục thành công!</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {aiDraft.length === 0 ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Đoạn văn bản cần xử lý</label>
                      <textarea rows={10} value={aiText} onChange={e => setAiText(e.target.value)}
                        placeholder={"Paste nội dung bất kỳ vào đây...\n\nVí dụ:\n- Chính sách giao hàng, đổi trả\n- Mô tả sản phẩm, dịch vụ\n- Nội dung từ fanpage, website\n- Tài liệu Word, PDF đã copy"}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-violet-300 resize-none" />
                      {aiError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg mt-2">{aiError}</p>}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">
                          AI trích xuất được <strong>{aiDraft.length}</strong> mục — bỏ chọn những mục không cần:
                        </p>
                        <button onClick={() => { setAiDraft([]); setAiError(''); }}
                          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
                          <X size={12} /> Làm lại
                        </button>
                      </div>
                      <div className="space-y-2">
                        {aiDraft.map((item, i) => (
                          <div key={i} onClick={() => toggleAIDraft(i)}
                            className={`border rounded-xl p-3 cursor-pointer transition-all select-none ${item._include !== false ? 'border-violet-200 bg-violet-50/40' : 'border-gray-100 bg-gray-50 opacity-50'}`}>
                            <div className="flex items-start gap-2">
                              <div className={`w-4 h-4 rounded border-2 shrink-0 mt-0.5 flex items-center justify-center transition-colors ${item._include !== false ? 'border-violet-500 bg-violet-500' : 'border-gray-300'}`}>
                                {item._include !== false && <Check size={10} className="text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">{item.question}</p>
                                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.answer}</p>
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                  {item.category && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-600">{item.category}</span>}
                                  {item.keywords.map(kw => <span key={kw} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-500">#{kw}</span>)}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 shrink-0">
                  <button onClick={() => setShowAI(false)} className="px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">Huỷ</button>
                  {aiDraft.length === 0 ? (
                    <button onClick={handleAIExtract} disabled={aiLoading || !aiText.trim()}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed">
                      <Sparkles size={14} /> {aiLoading ? 'Đang phân tích...' : 'Trích xuất Q&A'}
                    </button>
                  ) : (
                    <button onClick={handleAIImport} disabled={aiImporting || selectedAI === 0}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-violet-500 text-white hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed">
                      <Save size={14} /> {aiImporting ? 'Đang lưu...' : `Lưu ${selectedAI} mục`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* ── Modal: Phỏng vấn AI ──────────────────────────────────────────── */}
      {showInterview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} className="text-emerald-500" />
                <h2 className="text-base font-semibold text-gray-800">Phỏng vấn AI</h2>
                <span className="text-xs text-gray-400">AI sẽ hỏi bạn về doanh nghiệp để tạo Q&A tự động</span>
              </div>
              <button onClick={() => setShowInterview(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-500"><X size={16} /></button>
            </div>

            {!ivDone ? (
              <>
                {/* Chat messages */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {ivHistory.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.role === 'user'
                          ? 'bg-emerald-500 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {ivLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-500 px-3.5 py-2.5 rounded-2xl rounded-bl-md text-sm">
                        <span className="inline-flex gap-1"><span className="animate-bounce">.</span><span className="animate-bounce" style={{animationDelay:'0.1s'}}>.</span><span className="animate-bounce" style={{animationDelay:'0.2s'}}>.</span></span>
                      </div>
                    </div>
                  )}
                  <div ref={ivEndRef} />
                </div>

                {/* Input */}
                <div className="px-5 py-3 border-t border-gray-100 shrink-0">
                  {ivError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg mb-2">{ivError}</p>}
                  <div className="flex gap-2">
                    <input type="text" value={ivInput}
                      onChange={e => setIvInput(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendInterviewMsg(); } }}
                      placeholder="Nhập câu trả lời..."
                      disabled={ivLoading}
                      className="flex-1 px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-emerald-300 disabled:opacity-50" />
                    <button onClick={sendInterviewMsg} disabled={ivLoading || !ivInput.trim()}
                      className="px-3.5 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      <Send size={16} />
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1.5">Nhập "xong" khi bạn đã cung cấp đủ thông tin</p>
                </div>
              </>
            ) : (
              <>
                {/* Review items */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {ivSaved > 0 ? (
                    <div className="flex flex-col items-center justify-center py-10">
                      <Check size={40} className="text-emerald-500 mb-2" />
                      <p className="text-sm font-medium text-gray-700">Đã lưu {ivSaved} mục!</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 font-medium">AI đã tạo {ivItems.length} cặp Q&A{ivProducts.length > 0 ? ` và ${ivProducts.length} sản phẩm/dịch vụ` : ''}. Chọn những mục bạn muốn lưu:</p>

                      {/* Q&A items */}
                      {ivItems.map((it, i) => (
                        <label key={i} className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                          it._include ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50/50 opacity-60'
                        }`}>
                          <input type="checkbox" checked={it._include ?? true}
                            onChange={() => setIvItems(arr => arr.map((x, j) => j === i ? { ...x, _include: !x._include } : x))}
                            className="mt-0.5 w-4 h-4 accent-emerald-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800">Q: {it.question}</p>
                            <p className="text-sm text-gray-600 mt-0.5">A: {it.answer}</p>
                            <div className="flex gap-1.5 mt-1.5 flex-wrap">
                              {it.category && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">{it.category}</span>}
                              {(it.keywords || []).map(kw => <span key={kw} className="px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-500">#{kw}</span>)}
                            </div>
                          </div>
                        </label>
                      ))}

                      {/* Products/Services */}
                      {ivProducts.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 mt-4 mb-1">
                            <Package size={14} className="text-blue-500" />
                            <p className="text-sm text-gray-600 font-medium">Sản phẩm / Dịch vụ</p>
                          </div>
                          {ivProducts.map((p, i) => (
                            <label key={`p-${i}`} className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                              p._include ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-gray-50/50 opacity-60'
                            }`}>
                              <input type="checkbox" checked={p._include ?? true}
                                onChange={() => setIvProducts(arr => arr.map((x, j) => j === i ? { ...x, _include: !x._include } : x))}
                                className="mt-0.5 w-4 h-4 accent-blue-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800">{p.name}</p>
                                {p.description && <p className="text-sm text-gray-600 mt-0.5">{p.description}</p>}
                                {p.price && <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">{p.price}</span>}
                              </div>
                            </label>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
                {ivSaved === 0 && (
                  <div className="flex justify-between px-5 py-4 border-t border-gray-100 shrink-0">
                    <button onClick={() => { setIvDone(false); setIvItems([]); setIvProducts([]); }}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600">
                      <RotateCcw size={14} /> Hỏi thêm
                    </button>
                    <button onClick={handleIvImport} disabled={ivImporting || (ivItems.filter(r => r._include).length + ivProducts.filter(r => r._include).length) === 0}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed">
                      <Save size={14} /> {ivImporting ? 'Đang lưu...' : `Lưu ${ivItems.filter(r => r._include).length + ivProducts.filter(r => r._include).length} mục`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Lightbox ───────────────────────────────────────────────────────── */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full p-1.5"
            onClick={() => setLightboxSrc(null)}
          >
            <X size={22} />
          </button>
          <img
            src={lightboxSrc}
            alt="preview"
            className="max-w-[90vw] max-h-[88vh] rounded-xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};